// pages/monitor/StationMonitor.js —— 单站控制台入口（仅做：站点选择 + 电站类型分派 + 二次口令 gate）
// 评审 66：单站监控必须按电站类型分别依据单一需求文档开发，禁止三份文档混用 ——
//   削峰填谷（type 'peak'） → 《云平台单站控制（削峰填谷）需求文档》    → consoles/peak/PeakConsole
//   光储充  （type 'pvc'）  → 《云平台单站控制（光储充）需求文档》      → consoles/pvc/PvcConsole
//   其它    （如 'ems'）    → 《云平台（能源管家）需求文档》2.7 单站监控 → consoles/ems/EmsConsole
// 三套控制台各自组织 tab 集合与模块，互不串用。
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, Input, Button, Tag, message } from 'antd';
import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import PageHead from '../../components/PageHead';
import { STATIONS, getStation } from '../../config/stationData';
import PeakConsole from './consoles/peak/PeakConsole';
import PvcConsole from './consoles/pvc/PvcConsole';
import EmsConsole from './consoles/ems/EmsConsole';

// 演示口令（正式环境由后端校验，这里仅做界面演示）
const CONTROL_PWD = '123456';

// 光储充站点首页标题栏：右侧展示当前时间 + 天气 + 策略运行状态（光储充文档 p2）
const WEATHER = '晴 26 ℃';
const pad = (n) => String(n).padStart(2, '0');
const fmtClock = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

const COMM_TEXT = { normal: '通信正常', delay: '通信延迟', offline: '通信离线' };
const COMM_COLOR = { normal: 'green', delay: 'orange', offline: 'red' };

// 电站类型 → 控制台 + 依据的需求文档
const DOC_OF = {
  peak: '《云平台单站控制（削峰填谷）需求文档》',
  pvc: '《云平台单站控制（光储充）需求文档》',
  ems: '《云平台（能源管家）需求文档》2.7 单站监控',
};
const consoleOf = (type) => (type === 'peak' ? 'peak' : type === 'pvc' ? 'pvc' : 'ems');

const StationMonitor = () => {
  // 支持从站点地图 / 站点列表「进入监控」时通过 ?station=xxx 指定站点
  const [searchParams] = useSearchParams();
  const paramStation = searchParams.get('station');
  const [stationId, setStationId] = useState(
    STATIONS.some((s) => s.id === paramStation) ? paramStation : STATIONS[0].id,
  );
  const [unlocked, setUnlocked] = useState(false);
  const [pwd, setPwd] = useState('');
  const [rwUnlocked, setRwUnlocked] = useState(false);
  const [rwPwd, setRwPwd] = useState('');

  // 从站点地图再次「进入监控」时，同步 ?station= 指定的站点，保证与地图所选站点一致
  useEffect(() => {
    if (paramStation && STATIONS.some((s) => s.id === paramStation)) setStationId(paramStation);
  }, [paramStation]);

  const station = getStation(stationId);
  const kind = consoleOf(station.type);

  // 当前时间（光储充标题栏展示）
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (kind !== 'pvc') return undefined;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [kind]);

  // 切换站点后重新上锁，避免跨站点带着已解锁状态进入控制模块
  const handleStationChange = (v) => {
    setStationId(v);
    setUnlocked(false);
    setRwUnlocked(false);
  };

  const handleUnlock = () => {
    if (pwd === CONTROL_PWD) {
      setUnlocked(true);
      setPwd('');
      message.success('口令校验通过，控制操作已解锁');
    } else {
      message.error('口令错误（口令：123456）');
    }
  };

  const handleUnlockRW = () => {
    if (rwPwd === CONTROL_PWD) {
      setRwUnlocked(true);
      setRwPwd('');
      message.success('口令校验通过，设备读写已解锁');
    } else {
      message.error('口令错误（口令：123456）');
    }
  };

  const makeGate = (title, desc, value, onChange, onOk) => (
    <div className="sm-gate panel">
      <LockOutlined className="sm-gate-icon" />
      <div className="sm-gate-title">{title}</div>
      <div className="sm-gate-desc">
        {desc}
        <br />
        口令：<b>123456</b>
      </div>
      <div className="sm-gate-form">
        <Input.Password
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPressEnter={onOk}
          placeholder="请输入控制口令"
          style={{ width: 240 }}
        />
        <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={onOk}>
          校验并进入
        </Button>
      </div>
    </div>
  );

  // 控制中心 / 设备控制 二次口令 gate
  const gate = makeGate(
    '控制操作需要二次口令校验',
    kind === 'ems'
      ? '能源管家控制台将 PCS 开关机 / 复归、空调开关机放在「设备监控」内，为防止误操作需二次校验。'
      : '控制中心可下发策略与设备控制指令，为防止误操作需二次校验。',
    pwd,
    setPwd,
    handleUnlock,
  );

  // 设备读写 二次口令 gate（仅削峰填谷文档要求该模块）
  const rwGate = makeGate(
    '设备读写需要二次口令校验',
    '设备读写可直接下发点位写入指令，出于安全考虑需二次校验。口令默认与控制中心相同。',
    rwPwd,
    setRwPwd,
    handleUnlockRW,
  );

  const consoleNode = kind === 'peak'
    ? <PeakConsole station={station} unlocked={unlocked} gate={gate} rwUnlocked={rwUnlocked} rwGate={rwGate} />
    : kind === 'pvc'
      ? <PvcConsole station={station} unlocked={unlocked} gate={gate} />
      : <EmsConsole station={station} unlocked={unlocked} gate={gate} />;

  return (
    <div>
      <PageHead
        title="单站监控"
        desc={`当前依据 ${DOC_OF[kind]} 开发（${station.typeLabel}）`}
      />

      <div className="sm-bar">
        <span className="sm-bar-label">当前站点</span>
        <Select
          value={stationId}
          onChange={handleStationChange}
          style={{ width: 260 }}
          options={STATIONS.map((s) => ({
            value: s.id,
            label: (
              <span>
                {s.name}
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}> · {s.typeLabel}</span>
              </span>
            ),
          }))}
        />
        {kind === 'pvc' ? <Tag color="blue">{fmtClock(now)}</Tag> : null}
        {kind === 'pvc' ? <Tag color="gold">{WEATHER}</Tag> : null}
        <Tag color={COMM_COLOR[station.comm]}>{COMM_TEXT[station.comm]}</Tag>
        <Tag color={station.strategyOn ? 'cyan' : 'default'}>
          {station.strategyOn ? '策略已启用' : '策略未启用'}
        </Tag>
        <span className="sm-bar-meta">
          装机功率 {station.rated.power} · 装机容量 {station.rated.capacity} · {station.grid.voltage} · 投运 {station.onlineAt}
        </span>
      </div>

      {consoleNode}
    </div>
  );
};

export default StationMonitor;
